export type TimeFrame = 'day' | 'month' | 'year' | 'days' | 'months' | 'years';

export type CalculateTimeAgoProps = (date: Date) => {
  time: number;
  frame: TimeFrame;
};

/**
 * Calculates the time elapsed since a given date and returns the value in days, months, or years.
 *
 * The function normalizes both the current date and the input date to UTC to avoid timezone differences.
 * It then computes the difference in days and returns an object containing the time value and its corresponding frame
 * ('day(s)', 'month(s)', or 'year(s)').
 *
 * @param date - The date from which to calculate the elapsed time.
 * @returns An object with the elapsed time and its frame (e.g., `{ time: 5, frame: 'days' }`).
 */
export const calculateTimeAgo: CalculateTimeAgoProps = (date: Date) => {
  const now = new Date();

  // Convert both to UTC to remove timezone differences
  const nowUTC = new Date(now.toISOString());
  const dateUTC = new Date(date.toISOString());

  const diffInMs = nowUTC.getTime() - dateUTC.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays < 30) {
    return { time: diffInDays, frame: diffInDays === 1 ? 'day' : 'days' };
  } else if (diffInDays < 365) {
    // Calculate months based on year and month difference
    const months =
      (nowUTC.getUTCFullYear() - dateUTC.getUTCFullYear()) * 12 +
      (nowUTC.getUTCMonth() - dateUTC.getUTCMonth());
    return { time: months, frame: months === 1 ? 'month' : 'months' };
  } else {
    let years = nowUTC.getUTCFullYear() - dateUTC.getUTCFullYear();
    // Adjust if the current month/day is before the input month/day
    if (
      nowUTC.getUTCMonth() < dateUTC.getUTCMonth() ||
      (nowUTC.getUTCMonth() === dateUTC.getUTCMonth() &&
        nowUTC.getUTCDate() < dateUTC.getUTCDate())
    ) {
      years--;
    }
    return { time: years, frame: years === 1 ? 'year' : 'years' };
  }
};

/**
 * Calculates the time elapsed since the provided date and returns it in a structured format.
 *
 * If a valid date is provided, it returns the result of `calculateTimeAgo` for that date.
 * If no date is provided, it defaults to `{ time: 0, frame: 'days' }`.
 *
 * @param date - The date from which to calculate the elapsed time.
 * @returns An object representing the elapsed time and its frame (e.g., days, hours).
 */
export const getEditedTimeAgo: CalculateTimeAgoProps = (date: Date) => {
  if (isNaN(date.getTime())) {
    return { time: 0, frame: 'days' };
  }
  return calculateTimeAgo(date);
};
